import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import PageAlert from "../../../components/PageAlert/PageAlert";
import { resolveApiError } from "../../../utils/apiError";
import { getArticleMedia, getMediaUrl, isImageMedia, normalizePaginated } from "../articleApi";

const MediaLibraryModal = ({
  onClose,
  onSelect,
  title = "Media Library",
  description = "Select an image for this block.",
  imagesOnly = false,
}) => {
  const [keyword, setKeyword] = useState("");
  const [mediaItems, setMediaItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const loadMedia = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await getArticleMedia({
        per_page: 100,
        keyword: keyword || undefined,
        ...(imagesOnly ? { type: "image" } : {}),
      });
      setMediaItems(normalizePaginated(response).items);
    } catch (err) {
      resolveApiError(err, setError, undefined);
    } finally {
      setLoading(false);
    }
  }, [keyword, imagesOnly]);

  useEffect(() => {
    const timer = setTimeout(loadMedia, 250);
    return () => clearTimeout(timer);
  }, [loadMedia]);

  const filteredItems = useMemo(
    () =>
      mediaItems.filter((item) => {
        if (imagesOnly && !isImageMedia(item)) return false;
        const text = `${item.file_name || ""} ${item.alt_text || ""} ${item.caption || ""}`.toLowerCase();
        return text.includes(keyword.toLowerCase());
      }),
    [keyword, mediaItems, imagesOnly]
  );

  return (
    <div className="media-modal" role="dialog" aria-modal="true">
      <button type="button" className="media-modal__backdrop" aria-label="Close" onClick={onClose} />
      <div className="media-modal__panel">
        <div className="media-modal__header">
          <div>
            <h3>{title}</h3>
            <p>{description}</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close">
            <Icon icon="ph:x" height={16} />
          </button>
        </div>

        <div className="media-modal__toolbar">
          <input
            value={keyword}
            placeholder="Search media..."
            onChange={(event) => setKeyword(event.target.value)}
          />
        </div>

        {error && <PageAlert variant="error" onDismiss={() => setError("")}>{error}</PageAlert>}
        {loading && <div className="articles-page__loading">Loading media...</div>}

        <div className="media-modal__grid">
          {!loading &&
            filteredItems.map((item) => (
              <button
                type="button"
                className="media-modal__item"
                key={item.id}
                onClick={() => onSelect(item)}
              >
                <img src={getMediaUrl(item)} alt={item.alt_text || ""} />
                <span>{item.file_name || item.name}</span>
              </button>
            ))}

          {!loading && !filteredItems.length && (
            <div className="media-modal__empty">No media found.</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MediaLibraryModal;
